'use client';

import { c as _c } from "react/compiler-runtime";
import { cx } from 'class-variance-authority';
import { LuMapPin, LuTruck } from 'react-icons/lu';
import { HoverTooltip } from './HoverTooltip';
import { useI18n } from '~/features/i18n';
import type { WidgetMode } from '../types';
type Props = {
  value: WidgetMode;
  onChange: (next: WidgetMode) => void;
  /** Disables the "Aanbieder komt naar mij" tab when this partner doesn't travel. */
  customDisabled?: boolean;
  /** Disables the "Ik ga naar de aanbieder" tab when this partner has no fixed venue. */
  fixedDisabled?: boolean;
  className?: string;
};
const MODE_TAB_ICON: Record<WidgetMode, React.ComponentType<{
  className?: string;
}>> = {
  fixed: LuMapPin,
  custom: LuTruck
};
const MODE_TAB_ORDER: readonly WidgetMode[] = ['fixed', 'custom'];

/** Message ids and `message` defaults must be string literals so `lingui extract` picks them up. */
function modeTabLabel(i18n: ReturnType<typeof useI18n>, mode: WidgetMode): string {
  switch (mode) {
    case 'fixed':
      return i18n._('pdp.location.mode.fixed', undefined, {
        message: 'I travel to the provider'
      });
    case 'custom':
      return i18n._('pdp.location.mode.custom', undefined, {
        message: 'Provider comes to me'
      });
    default:
      {
        const _exhaustive: never = mode;
        return _exhaustive;
      }
  }
}
function modeTabTooltip(i18n: ReturnType<typeof useI18n>, mode: WidgetMode): string {
  switch (mode) {
    case 'fixed':
      return i18n._('pdp.location.mode.fixed.tooltip', undefined, {
        message: 'Find the closest provider near you'
      });
    case 'custom':
      return i18n._('pdp.location.mode.custom.tooltip', undefined, {
        message: 'The provider travels to your location — see the transport cost'
      });
    default:
      {
        const _exhaustive: never = mode;
        return _exhaustive;
      }
  }
}
export const ModeTabs: React.FC<Props> = t0 => {
  const $ = _c(11);
  const {
    value,
    onChange,
    customDisabled,
    fixedDisabled,
    className
  } = t0;
  const i18n = useI18n();
  let t1;
  if ($[0] !== className) {
    t1 = cx("grid grid-cols-2 gap-0.5 rounded-full bg-gray-100 p-0.5", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== customDisabled || $[3] !== fixedDisabled || $[4] !== i18n || $[5] !== onChange || $[6] !== value) {
    t2 = MODE_TAB_ORDER.map(mode => {
      const active = value === mode;
      const disabled = mode === "custom" && !!customDisabled || mode === "fixed" && !!fixedDisabled;
      const TabIcon = MODE_TAB_ICON[mode];
      return <HoverTooltip key={mode} content={disabled ? mode === "custom" ? i18n._("pdp.location.mode.custom.disabled", undefined, {
        message: "This provider does not travel to your location."
      }) : i18n._("pdp.location.mode.fixed.disabled", undefined, {
        message: "This provider only travels to your location."
      }) : modeTabTooltip(i18n, mode)} position="top" as="div"><button type="button" role="tab" aria-selected={active} aria-disabled={disabled} disabled={disabled} onClick={() => !disabled && onChange(mode)} className={cx("flex min-h-10 w-full items-center justify-center gap-1.5 rounded-full px-3 py-[15px] text-xs font-medium transition-colors duration-150 ease-out sm:text-sm", disabled ? "cursor-not-allowed text-blue-900/30 line-through decoration-blue-900/30 opacity-60" : active ? "bg-blue-900 text-white" : "text-blue-900 hover:bg-gray-50")} data-testid={`pdp-location-mode-${mode}`}><TabIcon className="hidden size-4 sm:block" /><span>{modeTabLabel(i18n, mode)}</span></button></HoverTooltip>;
    });
    $[2] = customDisabled;
    $[3] = fixedDisabled;
    $[4] = i18n;
    $[5] = onChange;
    $[6] = value;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== t1 || $[9] !== t2) {
    t3 = <div className={t1} role="tablist">{t2}</div>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  return t3;
};